import { useMemo, useState } from 'react';
import type { OfferedAction } from '@core/engine';
import type { ActionGroup } from '@core/actions';
import type { BoardNode } from '@core/types';
import './ActionBar.css';

const GROUP_LABEL: Record<ActionGroup, string> = {
  career: 'Work',
  education: 'Study',
  finance: 'Money',
  invest: 'Invest',
  business: 'Business',
  relationships: 'People',
  family: 'Family',
  health: 'Health',
  leisure: 'Time',
  retire: 'Retire',
};

interface Props {
  node: BoardNode;
  actions: OfferedAction[];
  actionUsed: boolean;
  canMove: boolean;
  onAct: (actionId: string) => void;
  onConclude: () => void;
}

/**
 * The action bar is where deliberate choices live, as opposed to events, which
 * happen *to* the player. One action per node is the whole opportunity-cost
 * mechanism: investing this year means not resting this year.
 */
export function ActionBar({ node, actions, actionUsed, canMove, onAct, onConclude }: Props) {
  const [open, setOpen] = useState<ActionGroup | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<ActionGroup, OfferedAction[]>();
    for (const item of actions) {
      const list = map.get(item.action.group) ?? [];
      list.push(item);
      map.set(item.action.group, list);
    }
    return [...map.entries()];
  }, [actions]);

  const activeList = open ? (grouped.find(([g]) => g === open)?.[1] ?? []) : [];

  return (
    <div className="actionbar">
      <div className="actionbar__context">
        <span className="eyebrow">You are here</span>
        <strong className="actionbar__node">{node.title}</strong>
        {node.subtitle && <span className="actionbar__sub">{node.subtitle}</span>}
      </div>

      {grouped.length > 0 && (
        <div className="actionbar__groups" role="group" aria-label="Things you can do">
          {actionUsed ? (
            <span className="actionbar__spent">
              You have used this year’s effort. Move forward when ready.
            </span>
          ) : (
            grouped.map(([group, items]) => (
              <button
                key={group}
                type="button"
                className={`actionbar__group${open === group ? ' actionbar__group--open' : ''}`}
                onClick={() => setOpen(open === group ? null : group)}
                aria-expanded={open === group}
              >
                {GROUP_LABEL[group]}
                <span className="actionbar__count">{items.length}</span>
              </button>
            ))
          )}
        </div>
      )}

      <div className="actionbar__tail">
        {canMove && <span className="actionbar__hint">Pick a step on the board to move forward</span>}
        <button type="button" className="btn btn--ghost btn--small" onClick={onConclude}>
          End this life
        </button>
      </div>

      {open && activeList.length > 0 && (
        <>
          <div className="actionbar__scrim" onClick={() => setOpen(null)} aria-hidden="true" />
          <div className="actionsheet" role="dialog" aria-label={`${GROUP_LABEL[open]} options`}>
            <div className="actionsheet__head">
              <h3 className="actionsheet__title">{GROUP_LABEL[open]}</h3>
              <span className="actionsheet__note">One choice, this year only</span>
              <button
                type="button"
                className="actionsheet__close"
                onClick={() => setOpen(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ul className="actionsheet__list">
              {activeList.map(({ action }) => (
                <li key={action.id}>
                  <button
                    type="button"
                    className="choice"
                    onClick={() => {
                      setOpen(null);
                      onAct(action.id);
                    }}
                  >
                    <span className="choice__main">
                      <span className="choice__label">{action.label}</span>
                      <span className="choice__detail">{action.detail}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
